/**
 * src/pages/WhereIsEverythingGoing/sections/FateOfUniverseSection.tsx
 * "Fate of the universe": comparison of scenarios using cards/tabs with a small animated diagram each:
 * Heat death / Big freeze, Big Rip, Big Crunch, Vacuum decay, Big Bounce / cyclic.
 * Explain observations: expansion accelerating (Riess 1998; Perlmutter 1999), dark energy ~68% (Planck 2018),
 * nature unknown; DESI 2024-25 hints dark energy might evolve with time (research).
 * Includes an interactive "dark energy dial" (w from -1.4 to -0.6) that highlights which scenario each setting
 * points to, stating it is a simplified educational illustration, not a prediction.
 */

import React, { useState } from "react";
import { COSMIC_SCENARIOS } from "../futureData";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Sparkles, Sliders, Info, Compass, ShieldAlert } from "lucide-react";
export function FateOfUniverseSection() {
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>("heat-death");
  // Dark Energy w parameter dial: default -1.0 (Cosmological Constant)
  const [wValue, setWValue] = useState<number>(-1.0);
  const activeScenario = COSMIC_SCENARIOS.find(s => s.id === selectedScenarioId) || COSMIC_SCENARIOS[0];

  // Map wValue to recommended scenario
  const getScenarioFromW = (w: number): string => {
    if (w < -1.05) return "big-rip";
    if (w >= -1.05 && w <= -0.95) return "heat-death";
    return "big-crunch"; // if w > -0.95 or approaching matter/curvature domination
  };
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setWValue(val);
    const suggestedId = getScenarioFromW(val);
    setSelectedScenarioId(suggestedId);
  };
  return <section id="fate-of-universe" className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30" aria-labelledby="fate-heading">
      <div className="max-w-[76rem] mx-auto min-w-0">
        <SectionHeading eyebrow="Cosmological Dynamics & Geometry" title="Fate of the Universe" description="In 1998, observations of distant Type Ia supernovae proved that cosmic expansion is accelerating. Today, roughly 68% of the universe consists of dark energy whose fundamental equation of state will determine whether cosmos freezes, rips, or collapses." />

        {/* Observational Evidence Callout Panel */}
        <div className="ql-glass p-5 sm:p-6 mb-8 rounded-2xl border border-primary/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs sm:text-sm text-muted-foreground font-sans min-w-0">
          <div className="flex items-start gap-3 min-w-0">
            <Info className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-foreground uppercase tracking-wider text-xs">
                  Empirical Baseline:
                </span>
                <EvidenceBadge level="established" compact />
              </div>
              <p className="leading-relaxed break-words">
                Cosmic acceleration discovered 1998–1999 (Riess et al.<Footnote n={3} />;
                Perlmutter et al.<Footnote n={4} />). Planck 2018 cosmic microwave
                background measurements establish dark energy density at ~68.3% of the
                total cosmic budget<Footnote n={5} />.
              </p>
              <p className="leading-relaxed break-words text-primary/90">
                <strong>Recent Discovery (2024–2025):</strong> Baryon Acoustic
                Oscillation data from the Dark Energy Spectroscopic Instrument (DESI)
                provides tentative hints that dark energy may dynamically evolve over
                time, though findings remain actively debated and non-conclusive
                <Footnote n={7} />.
              </p>
            </div>
          </div>
        </div>

        {/* Interactive Dark Energy Dial */}
        <div className="ql-glass-strong p-6 sm:p-8 rounded-2xl border border-primary/30 mb-10 flex flex-col gap-5 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border/30">
            <div className="flex items-center gap-2 min-w-0">
              <Sliders className="size-4 text-primary shrink-0" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
                Dark Energy Parameter Dial (w)
              </span>
            </div>
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <span>Equation of State: </span>
              <span className="text-primary font-bold text-sm">
                w = {wValue.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex justify-between text-xs font-mono text-muted-foreground flex-wrap gap-1">
              <span className="text-rose-400">w = −1.40 (Phantom Energy / Big Rip)</span>
              <span className="text-primary font-bold">
                w = −1.00 (Cosmological Constant Λ / Heat Death)
              </span>
              <span className="text-amber-400">
                w = −0.60 (Weakening / Quintessence)
              </span>
            </div>

            <div className="py-2 flex items-center min-h-[44px]">
              <input type="range" min={-1.4} max={-0.6} step={0.05} value={wValue} onChange={handleSliderChange} className="ob-timeline-slider w-full touch-pan-y min-h-[44px] cursor-pointer" aria-label="Dark energy equation of state parameter w from -1.4 to -0.6" aria-valuetext={`Dark energy w equals ${wValue.toFixed(2)}`} />
            </div>

            <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground pt-1 flex-wrap gap-1">
              <span>Repulsive Force Diverges</span>
              <span>Constant Vacuum Energy Density</span>
              <span>Gravitational Deceleration Re-emerges</span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground font-sans italic bg-card/40 p-3 rounded-lg border border-border/40">
            * Educational simulation note: This slider is a simplified pedagogical model
            demonstrating how small alterations in the dark energy equation of state
            influence universal cosmic geometry. It is not an observational prediction.
          </p>
        </div>

        {/* Scenarios Grid / Selectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8 min-w-0">
          {COSMIC_SCENARIOS.map(scenario => {
          const isSelected = scenario.id === selectedScenarioId;
          return <button key={scenario.id} type="button" onClick={() => setSelectedScenarioId(scenario.id)} className={`p-4 rounded-xl text-left border transition-all flex flex-col justify-between gap-3 min-h-[110px] min-w-0 ${isSelected ? "bg-primary/15 border-primary shadow-[0_0_16px_var(--glow-primary)] ring-1 ring-primary" : "bg-card/40 border-border/50 hover:border-primary/50 hover:bg-card/70"}`} aria-pressed={isSelected}>
                <div>
                  <div className="flex items-center justify-between gap-1 mb-1.5">
                    <EvidenceBadge level={scenario.evidence} compact />
                  </div>
                  <h4 className="font-serif text-sm font-bold text-foreground leading-snug break-words">
                    {scenario.name}
                  </h4>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground break-words leading-tight" title={scenario.equationState}>
                  {scenario.equationState}
                </span>
              </button>;
        })}
        </div>

        {/* Selected Scenario Detailed Showcase */}
        <div className="ql-glass p-6 sm:p-8 lg:p-10 rounded-2xl border border-primary/40 flex flex-col lg:flex-row items-center gap-8 min-w-0">
          {/* Animated Mini-Diagram Canvas (Left 4 cols) */}
          <div className="w-full lg:w-1/3 flex flex-col items-center justify-center p-6 rounded-xl bg-card/60 border border-border/60 min-w-0 shrink-0">
            <div className="relative w-full max-w-[176px] sm:max-w-[192px] aspect-square flex items-center justify-center overflow-hidden">
              {/* Heat Death: Expanding dilute grid */}
              {activeScenario.id === "heat-death" && <svg viewBox="0 0 160 160" className="w-full h-full max-w-full h-auto" fill="none" aria-hidden="true">
                  <circle cx="80" cy="80" r="70" stroke="hsl(186 100% 50% / 0.2)" strokeWidth="1" strokeDasharray="4 4" />
                  <circle cx="80" cy="80" r="45" stroke="hsl(186 100% 50% / 0.4)" strokeWidth="1" />
                  <circle cx="80" cy="80" r="6" fill="hsl(186 100% 50%)" className="animate-pulse" />
                  <path d="M 80 30 L 80 15 M 80 130 L 80 145 M 30 80 L 15 80 M 130 80 L 145 80" stroke="hsl(186 100% 50%)" strokeWidth="1.5" />
                </svg>}

              {/* Big Rip: Exploding vector field */}
              {activeScenario.id === "big-rip" && <svg viewBox="0 0 160 160" className="w-full h-full max-w-full h-auto" fill="none" aria-hidden="true">
                  <circle cx="80" cy="80" r="75" stroke="hsl(0 84% 60% / 0.4)" strokeWidth="1.5" strokeDasharray="2 3" />
                  <circle cx="80" cy="80" r="30" stroke="hsl(0 84% 60% / 0.7)" strokeWidth="2" />
                  <line x1="40" y1="40" x2="15" y2="15" stroke="hsl(0 84% 60%)" strokeWidth="2" />
                  <line x1="120" y1="40" x2="145" y2="15" stroke="hsl(0 84% 60%)" strokeWidth="2" />
                  <line x1="40" y1="120" x2="15" y2="145" stroke="hsl(0 84% 60%)" strokeWidth="2" />
                  <line x1="120" y1="120" x2="145" y2="145" stroke="hsl(0 84% 60%)" strokeWidth="2" />
                </svg>}

              {/* Big Crunch: Collapsing inward arrows */}
              {activeScenario.id === "big-crunch" && <svg viewBox="0 0 160 160" className="w-full h-full max-w-full h-auto" fill="none" aria-hidden="true">
                  <circle cx="80" cy="80" r="65" stroke="hsl(38 95% 55% / 0.4)" strokeWidth="1.5" />
                  <circle cx="80" cy="80" r="25" stroke="hsl(38 95% 55% / 0.8)" strokeWidth="2" />
                  <circle cx="80" cy="80" r="8" fill="hsl(38 95% 60%)" />
                  <path d="M 20 80 L 45 80 M 140 80 L 115 80 M 80 20 L 80 45 M 80 140 L 80 115" stroke="hsl(38 95% 60%)" strokeWidth="2" />
                </svg>}

              {/* Vacuum Decay: Expanding true vacuum bubble */}
              {activeScenario.id === "vacuum-decay" && <svg viewBox="0 0 160 160" className="w-full h-full max-w-full h-auto" fill="none" aria-hidden="true">
                  <rect x="10" y="10" width="140" height="140" stroke="hsl(270 70% 55% / 0.3)" strokeWidth="1" strokeDasharray="4 4" />
                  <circle cx="80" cy="80" r="48" fill="hsl(270 70% 55% / 0.2)" stroke="hsl(270 70% 65%)" strokeWidth="2" />
                  <circle cx="80" cy="80" r="10" fill="hsl(270 70% 75%)" />
                  <text x="80" y="115" textAnchor="middle" fill="hsl(270 70% 80%)" fontSize="8" fontFamily="var(--font-mono)">
                    TRUE VACUUM c
                  </text>
                </svg>}

              {/* Big Bounce: Oscillating hourglass wave */}
              {activeScenario.id === "big-bounce" && <svg viewBox="0 0 160 160" className="w-full h-full max-w-full h-auto" fill="none" aria-hidden="true">
                  <path d="M 20 30 Q 80 80 20 130 M 140 30 Q 80 80 140 130" stroke="hsl(215 100% 60%)" strokeWidth="2" />
                  <circle cx="80" cy="80" r="12" fill="hsl(215 100% 70% / 0.4)" stroke="hsl(215 100% 60%)" strokeWidth="1.5" />
                  <text x="80" y="84" textAnchor="middle" fill="hsl(0 0% 98%)" fontSize="7" fontFamily="var(--font-mono)">
                    BOUNCE
                  </text>
                </svg>}
            </div>

            <span className="font-mono text-[11px] text-primary tracking-wider uppercase mt-3">
              Geometry: {activeScenario.name}
            </span>
          </div>

          {/* Descriptive Content (Right 8 cols) */}
          <div className="flex-1 space-y-4 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <EvidenceBadge level={activeScenario.evidence} />
              <span className="font-mono text-xs px-2.5 py-1 rounded bg-card border border-border text-primary font-semibold">
                {activeScenario.badgeText}
              </span>
            </div>

            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground tracking-tight break-words">
              {activeScenario.title}
            </h3>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans break-words">
              {activeScenario.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 font-sans text-xs">
              <div className="p-3.5 rounded-xl bg-card/40 border border-border/40">
                <span className="font-mono text-primary font-bold block uppercase mb-1">
                  Driving Physical Mechanism
                </span>
                <span className="text-muted-foreground">{activeScenario.mechanism}</span>
              </div>
              <div className="p-3.5 rounded-xl bg-card/40 border border-border/40">
                <span className="font-mono text-primary font-bold block uppercase mb-1">
                  Estimated Horizon
                </span>
                <span className="text-muted-foreground">{activeScenario.timeframe}</span>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-card/30 border border-border/30 text-xs text-muted-foreground flex items-center gap-2">
              <Compass className="size-4 text-primary shrink-0" />
              <span>
                <strong>Epistemic Status:</strong> {activeScenario.statusText}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}